import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { format } from "date-fns";
import { CreditCard, CheckCircle, Clock, Warning, FilePdf } from "@phosphor-icons/react";

const fmtMXN = (v) => (v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_LABELS = {
  vigente: { label: "Vigente", color: "bg-emerald-100 text-emerald-700" },
  por_vencer: { label: "Por vencer", color: "bg-amber-100 text-amber-700" },
  vencida: { label: "Vencida", color: "bg-red-100 text-red-700" },
  pago_parcial: { label: "Pago parcial", color: "bg-blue-100 text-blue-700" },
  pagada: { label: "Pagada", color: "bg-slate-100 text-slate-500" },
};

export default function CxCPortalView({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/cxc/portal/${token}`)
      .then(res => setData(res.data))
      .catch(e => {
        if (e.response?.status === 410) setError("expired");
        else if (e.response?.status === 404) setError("not_found");
        else setError("error");
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin w-10 h-10 border-4 border-sky-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-sm">
        {error === "expired" ? (
          <>
            <Clock size={48} className="mx-auto mb-3 text-amber-500" />
            <p className="text-amber-700 font-bold text-lg">Enlace Expirado</p>
            <p className="text-sm text-slate-500 mt-2">Este enlace ya no es valido. Solicita uno nuevo a tu proveedor.</p>
          </>
        ) : (
          <>
            <Warning size={48} className="mx-auto mb-3 text-red-500" />
            <p className="text-red-600 font-bold text-lg">Enlace no encontrado</p>
            <p className="text-sm text-slate-500 mt-2">El enlace puede ser incorrecto o haber sido eliminado.</p>
          </>
        )}
      </div>
    </div>
  );

  const { client, summary, accounts, payments, config, payment_method_labels } = data;
  const pendingAccounts = accounts.filter(a => a.balance > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-6 px-4" data-testid="portal-view">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-sky-700 px-6 py-5 text-white">
            <div className="flex items-center gap-3">
              <CreditCard size={28} weight="duotone" />
              <div>
                <p className="text-xs uppercase tracking-widest opacity-80">Estado de Cuenta</p>
                {config?.company_name && <p className="text-sm font-bold mt-0.5">{config.company_name}</p>}
              </div>
            </div>
          </div>
          <div className="px-6 py-4">
            <h2 className="text-lg font-bold text-slate-900">{client.company_name}</h2>
            {client.contact_name && <p className="text-xs text-slate-500">{client.contact_name}</p>}
            <p className="text-[10px] text-slate-400 mt-1">Credito: {client.credit_days} dias &middot; Limite: ${fmtMXN(client.credit_limit)}</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <p className="text-[10px] text-slate-400 uppercase font-bold">Saldo Total</p>
            <p className="text-xl font-extrabold text-red-600 font-mono mt-1">${fmtMXN(summary.total_due)}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <p className="text-[10px] text-slate-400 uppercase font-bold">Vencido</p>
            <p className="text-xl font-extrabold text-amber-600 font-mono mt-1">${fmtMXN(summary.total_overdue)}</p>
          </div>
          <div className="bg-white rounded-xl shadow p-4 text-center">
            <p className="text-[10px] text-slate-400 uppercase font-bold">Cuentas</p>
            <p className="text-xl font-extrabold text-sky-700 font-mono mt-1">{summary.total_accounts}</p>
          </div>
        </div>

        {/* Pending Accounts */}
        {pendingAccounts.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-5 py-3 border-b bg-slate-50">
              <h3 className="text-sm font-bold text-slate-700">Facturas Pendientes</h3>
            </div>
            <div className="divide-y">
              {pendingAccounts.map((a, i) => {
                const st = STATUS_LABELS[a.status] || STATUS_LABELS.vigente;
                return (
                  <div key={i} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold text-sky-700">{a.folio}</span>
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                      </div>
                      <div className="flex gap-3 mt-0.5 text-[10px] text-slate-500">
                        <span>Total: ${fmtMXN(a.total)}</span>
                        <span>Pagado: ${fmtMXN(a.paid)}</span>
                        <span>Vence: {a.due_date ? format(new Date(a.due_date), "dd/MM/yy") : "-"}</span>
                        {a.days_overdue > 0 && a.status !== "pagada" && (
                          <span className="text-red-600 font-bold">{a.days_overdue}d vencida</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900 font-mono">${fmtMXN(a.balance)}</p>
                      {a.public_token && (
                        <a href={`/venta/${a.public_token}`} target="_blank" rel="noopener noreferrer" className="text-[9px] text-sky-600 hover:underline">
                          Ver documento
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Payments */}
        {payments.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-5 py-3 border-b bg-slate-50">
              <h3 className="text-sm font-bold text-slate-700">Pagos Recientes</h3>
            </div>
            <div className="divide-y">
              {payments.slice(0, 10).map((p, i) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-mono text-sky-700 font-bold">{p.sale_folio}</span>
                    <span className="text-[10px] text-slate-400 ml-2">
                      {p.created_at ? format(new Date(p.created_at), "dd/MM/yy") : ""} &middot;
                      {payment_method_labels?.[p.payment_method] || p.payment_method}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-emerald-600 font-mono">${fmtMXN(p.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-[10px] text-slate-400 pb-4">
          <p>Estado de cuenta generado por el sistema</p>
          {data.expires_at && <p>Enlace valido hasta: {format(new Date(data.expires_at), "dd/MM/yyyy")}</p>}
        </div>
      </div>
    </div>
  );
}
